import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, inject as ngInject } from '@angular/core';
import { ComponentStore, provideComponentStore } from '@ngrx/component-store';
import { TranslateService } from '@ngx-translate/core';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef, GridOptions } from 'ag-grid-community';

import { inject } from '../../app-injector';
import { UserService } from '../../services/user-service/user.service';
import { AgGridService } from '../ag-grid/ag-grid.service';

export const GRID_OPTIONS: GridOptions = {
  rowHeight: 35,
  suppressCellFocus: true,
  enableCellTextSelection: true,
  ensureDomOrder: true,
};

const DEFAULT_COL_DEF: ColDef<any> = {
  resizable: true,
  sortable: true,
  flex: 1,
  filter: true, // true for default filter
};

const initialState: ComponentState = {
  rowData: [],
  columnDefs: [],
  defaultColDef: DEFAULT_COL_DEF,
  loading: false,
};

interface ComponentState {
  rowData: any[];
  columnDefs: ColDef<any>[];
  defaultColDef: ColDef<any>;
  loading: boolean;
}

export type ColDefTemplate<T = any> = Omit<ColDef, 'headerName' | 'headerTooltip' | 'field'> & {
  /**
   * will be converted to `headerName` and `headerTooltip`
   */
  headerNameAndTooltipKey: string;
  /**
   * will be converted to (untyped) `field`
   * with this typed field property it's indeed typed checked against the given object.
   */
  typedField?: keyof T;
};

export const fromColDefTemplates = (colDefTemplates: ColDefTemplate[]): ColDef[] =>
  colDefTemplates.map((item: ColDefTemplate): ColDef => {
    const translation =
      item?.headerNameAndTooltipKey === undefined ? '' : inject(TranslateService).instant(item.headerNameAndTooltipKey);
    return {
      ...item,
      headerName: translation,
      headerTooltip: translation,
      field: item.typedField as string, // makes fields type safe
    };
  });

@Component({
  selector: 'app-grid',
  templateUrl: './the-grid.component.html',
  styleUrls: ['./the-grid.component.scss'],
  imports: [CommonModule, AgGridModule],
  providers: [provideComponentStore(ComponentStore)],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TheGridComponent {
  private readonly componentStore = ngInject<ComponentStore<ComponentState>>(ComponentStore);
  private readonly agGridService = ngInject(AgGridService);
  private readonly userService = ngInject(UserService);
  private readonly translateService = ngInject(TranslateService);

  public readonly rowData$ = this.componentStore.select(state => state.rowData);
  public readonly columnDefs$ = this.componentStore.select(state => state.columnDefs);
  public readonly defaultColumnDef$ = this.componentStore.select(state => state.defaultColDef);
  public readonly loading$ = this.componentStore.select(state => state.loading);

  public constructor() {
    this.componentStore.setState(initialState);
  }

  @Input()
  public set rowData(rowData: any[]) {
    this.componentStore.patchState({ rowData });
  }

  @Input()
  public set columnDefs(columnDefs: ColDef<any>[]) {
    this.componentStore.patchState({ columnDefs });
  }

  @Input()
  public set defaultColumnDef(defaultColDef: ColDef<any>) {
    this.componentStore.patchState({ defaultColDef });
  }

  @Input()
  public set loading(loading: boolean) {
    this.componentStore.patchState({ loading });
  }

  // TODO: dont pass services to other services, change this.
  public readonly gridOptions: GridOptions = {
    ...GRID_OPTIONS,
    getLocaleText: this.agGridService.translate(this.userService, this.translateService),
  };

  public onGridReady(): void {
    this.gridOptions.overlayNoRowsTemplate = `<span>${this.translateService.instant('noDataFound')}</span>`;
    this.gridOptions.overlayComponentParams = {
      noMatchingRows: { overlayText: this.translateService.instant('noMatchingRows') },
    };
  }
}
